<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <v-btn text color="primary" class="tw-mb-4" @click="$router.push({ name: 'AgentClientClaims' })">
          <v-icon left>mdi-arrow-left</v-icon>
          Back to Client Claims
        </v-btn>

        <div v-if="loading" class="tw-w-full tw-flex tw-justify-center tw-py-20">
          <v-progress-circular :size="56" color="#00AEEF" indeterminate />
        </div>

        <template v-else-if="claim && claim.claimNo">
          <!-- Page Header -->
          <header class="page-header tw-mb-6">
            <h2 class="page-title">Claim Details</h2>
          </header>

          <!-- Claim Info Card -->
          <div class="detail-card tw-mb-6">
            <h3 class="detail-section-title">Claim Information</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Client Name</span>
                <span class="detail-value">{{ claim.clientName || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Claim Number</span>
                <span class="detail-value">{{ claim.claimNo || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Policy Number</span>
                <span class="detail-value">{{ claim.policyNo || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Claiming Risk</span>
                <span class="detail-value">{{ claim.propertyId || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Cover Type</span>
                <span class="detail-value">{{ claim.coverTypeDesc || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Loss Description</span>
                <span class="detail-value">{{ claim.lossDesc || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Claim Cause</span>
                <span class="detail-value">{{ claim.claimCauseDesc || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Payments Table -->
          <div class="detail-card">
            <h3 class="detail-section-title">Payments</h3>
            <div v-if="payments.length > 0" class="payments-table-wrap">
              <table class="payments-table">
                <thead>
                  <tr>
                    <th>Voucher</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Payee</th>
                    <th>Comments</th>
                    <th>Raised Date</th>
                    <th>Date Auth GIS</th>
                    <th>Date Auth Finance</th>
                    <th>Payment Desc</th>
                    <th>Cheque No</th>
                    <th>Cheque Date</th>
                    <th>Cheque Amount</th>
                    <th>Net Paid</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, idx) in payments" :key="idx" class="payments-row">
                    <td>{{ p.voucher || '—' }}</td>
                    <td>{{ formatAmount(p.amount) }}</td>
                    <td><span class="status-badge">{{ p.paymentStatus || '—' }}</span></td>
                    <td>{{ p.payee || '—' }}</td>
                    <td>{{ p.comments || '—' }}</td>
                    <td>{{ p.raisedDate || '—' }}</td>
                    <td>{{ p.dateAuthGis || '—' }}</td>
                    <td>{{ p.dateAuthFinance || '—' }}</td>
                    <td>{{ p.paymentDesc || '—' }}</td>
                    <td>{{ p.chequeNo || '—' }}</td>
                    <td>{{ p.chequeDate || '—' }}</td>
                    <td>{{ formatAmount(p.chequeAmount) }}</td>
                    <td>{{ formatAmount(p.netPaid) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-payments">
              <v-icon size="48" color="#CBD5E1">mdi-cash-multiple</v-icon>
              <p>No payments recorded for this claim.</p>
            </div>
          </div>
        </template>

        <!-- Not Found -->
        <div v-else class="empty-state tw-py-16">
          <v-icon size="60" color="#CBD5E1">mdi-file-document-outline</v-icon>
          <p class="empty-title">Claim not found</p>
          <p class="empty-subtitle">Please go back and select a claim from the list.</p>
          <v-btn color="primary" outlined class="tw-mt-4" @click="$router.push({ name: 'AgentClientClaims' })">
            Back to Client Claims
          </v-btn>
        </div>

      </div>
    </div>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentClientClaimDetails',
  components: { AgentDefault },
  data() {
    return {
      loading: true,
      claim: null,
    };
  },
  computed: {
    claimId() {
      return this.$route.params.claimId;
    },
    payments() {
      const list = this.claim?.payments;
      return Array.isArray(list) ? list : [];
    },
  },
  watch: {
    claimId: {
      immediate: true,
      handler() {
        this.loadClaim();
      },
    },
  },
  mounted() {
    this.loadClaim();
  },
  methods: {
    loadClaim() {
      this.loading = true;
      try {
        const key = this.claimId ? `agentClaim_${this.claimId}` : null;
        const raw = key ? sessionStorage.getItem(key) : null;
        const parsed = raw ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : null;
        this.claim = parsed && parsed.claimNo ? parsed : null;
        trackAgentInteraction(this, 'claim_details_loaded', {
          claim_id: this.claimId || 'unknown',
          found: Boolean(this.claim),
        });
      } finally {
        this.loading = false;
      }
    },
    formatAmount(val) {
      if (val == null || val === undefined || val === '') return '—';
      return Number(val).toLocaleString();
    },
  },
  metaInfo: {
    title: 'Claim Details',
  },
};
</script>

<style scoped>
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0;
}

.detail-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  padding: 24px;
}

.detail-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #E8EDF2;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1E4276;
}

.payments-table-wrap {
  overflow-x: auto;
}

.payments-table {
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
}

.payments-table thead tr {
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.payments-table th {
  text-align: left;
  padding: 12px 14px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.payments-row {
  border-bottom: 1px solid #F1F5F9;
}

.payments-row:hover {
  background: #F8FAFC;
}

.payments-table td {
  padding: 14px;
  font-size: 0.875rem;
  color: #334155;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  background: #EBF8FF;
  color: #00AEEF;
}

.empty-payments {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #94A3B8;
  font-size: 0.95rem;
}

.empty-payments p {
  margin: 12px 0 0 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 16px 0 6px;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
</style>
